/**
 * What the web layer is running inside, and the three native capabilities it uses.
 *
 * Every export here is safe to call from a browser: `isNativePlatform` answers false and each
 * of the others turns into either nothing or its ordinary web equivalent. That is the whole
 * design rule for this directory — the game is a website first, and a Capacitor shell is one
 * more surface it is drawn on, never a second build of it. Nothing outside `src/native` imports
 * a Capacitor package.
 */

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { KeepAwake } from '@capacitor-community/keep-awake';

/** True only inside the iOS or Android shell. False in every browser, and in jsdom. */
export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

/** `'ios' | 'android' | 'web'`. Used for the two rules that genuinely differ by platform. */
export function nativePlatform(): string {
  return Capacitor.getPlatform();
}

/**
 * Whether the page may be drawn *under* the status bar on this platform.
 *
 * **iOS only, and that is the fix for the app bar sitting under the clock.** Overlaying is only
 * safe where the web layer can find out how tall the bar is, and on Android it cannot: Chromium
 * derives `env(safe-area-inset-*)` from the display cutout alone, so on the many phones with no
 * notch every edge reports 0 while the status bar is still painted over the page — `.safe-header`
 * then pads by nothing and the wordmark comes out behind the clock.
 *
 * On Android 15+ this call does nothing at all (`overlaysWebView` is documented as unavailable
 * there) and it does not need to: edge-to-edge is enforced, and Capacitor's own `SystemBars`
 * either insets the native view or publishes the real window insets as the
 * `--safe-area-inset-*` custom properties `app.css` reads. What this covers is Android 14 and
 * below, where nothing reports an inset and the only correct answer is to let the system lay the
 * WebView out beneath the bar.
 */
function shouldOverlayStatusBar(): boolean {
  return nativePlatform() === 'ios';
}

/**
 * Configure the status bar, once, at startup.
 *
 * `overlaysWebView` is what makes the shell edge-to-edge — the page is drawn *under* the bar and
 * the safe-area insets in `app.css` are what keep anything readable out from under it. It is set
 * in `capacitor.config.ts` as well; re-applying it here is what covers Android, where the
 * configured value is only the starting one, an OS theme change can move it back, and the answer
 * differs from iOS's (see `shouldOverlayStatusBar`).
 *
 * Failures are swallowed. A status bar that stayed light on a dark game is a blemish; an
 * exception thrown out of app startup is a white screen.
 */
export async function configureStatusBar(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setOverlaysWebView({ overlay: shouldOverlayStatusBar() });
  } catch {
    // Not every platform implements every call; none of them is worth failing a launch over.
  }
}

/**
 * Take the status bar away for the length of a match, and give it back afterwards.
 *
 * **THE ARENA IS THE ONE SCREEN WITH NOTHING TO SPARE AT THE TOP.** `targetSdk` is 36, so Android
 * draws this app edge-to-edge whatever `setOverlaysWebView` asks for, and the clock and the
 * battery are painted over the top of the arena: "the bar across the top in landscape mode when
 * playing has the time and battery and all the normal mobile stuff... not running into the text".
 * On a 390-pixel-tall phone that bar is 24 of them — six per cent of the screen, and enough to
 * cost the leaderboard a row on its own.
 *
 * So a live match hides it and everything else keeps it, which is the rule the app bar itself is
 * under (`stores/arena-chrome.ts`). What a player loses is the clock while they are playing, which
 * is what every fullscreen game on the phone does; what they get back is the top of the map.
 *
 * **IT IS NOT THE ONLY DEFENCE, AND IT MUST NOT BE.** `.hud-safe-inset` pays the top inset while
 * the arena is immersive, so a browser — which cannot hide anything — and an iOS build and the
 * frame before this call lands all put the HUD below whatever is there. When the bar does go, the
 * inset goes to zero by itself and the two compose at no cost.
 *
 * Failures are swallowed, `configureStatusBar`'s rule: a bar that stayed up is a blemish, and an
 * exception out of the arena's mount effect is a black screen.
 */
export async function hideStatusBar(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await StatusBar.hide();
  } catch {
    // No status bar to hide, or a platform that will not. `.hud-safe-inset` is the other half.
  }
}

/** Put it back. Called when the arena unmounts, by whatever route. See `hideStatusBar`. */
export async function showStatusBar(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await StatusBar.show();
  } catch {
    // Same as above: never worth failing a teardown over.
  }
}

/**
 * Whether the app is being looked at, and every change to that.
 *
 * Native and web are the same event wearing two names, so they are one function: a Capacitor
 * shell reports it as `appStateChange`, and a browser tab — which is throttled and can have its
 * socket closed under it in exactly the same way — reports it as `visibilitychange`.
 *
 * Both directions, not just the resume: a backgrounded phone should not be polling, which needs
 * the "we stopped being looked at" edge as much as the other one.
 *
 * Returns a disposer. On native the listener handle arrives asynchronously, so a caller that
 * unsubscribes before it resolves is handled here rather than by every caller.
 */
export function onAppVisibilityChange(handler: (visible: boolean) => void): () => void {
  if (!isNativePlatform()) {
    if (typeof document === 'undefined') return () => undefined;
    const onChange = () => handler(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }

  let handle: { remove: () => Promise<void> } | null = null;
  let cancelled = false;
  void App.addListener('appStateChange', ({ isActive }) => handler(isActive)).then((registered) => {
    if (cancelled) void registered.remove();
    else handle = registered;
  });

  return () => {
    cancelled = true;
    void handle?.remove();
  };
}

/** Whether the app is being looked at right now. `true` anywhere that cannot say otherwise. */
export function isAppVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

/**
 * Hold the screen awake for as long as the returned disposer has not been called.
 *
 * A match is played by steering with a thumb that is often *not* moving — a player running away
 * in a straight line touches nothing for ten seconds at a time — so the display timeout is a
 * real hazard rather than a theoretical one. Scoped to the arena being mounted, so the lock is
 * released by leaving the match, by dying and backing out, and by the app being torn down.
 *
 * A no-op in a browser: the Screen Wake Lock API is a different permission story on a page
 * nobody installed, and the card that asked for this asked for it in the shell.
 */
export function keepScreenAwake(): () => void {
  if (!isNativePlatform()) return () => undefined;
  void KeepAwake.keepAwake().catch(() => undefined);
  return () => {
    void KeepAwake.allowSleep().catch(() => undefined);
  };
}
